import React, { useState } from "react";
import { useObjectStore } from "../zustand/objects";
import { IconTable, IconLink, IconLayoutGrid, IconPresentation } from '@tabler/icons-react';
import { ActionIcon, Tooltip } from "@mantine/core";
import StoryboardToolbarIcon from "./StoryboardToolbarIcon";

const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

/** Safe for double-quoted HTML attribute values (e.g. img src). */
const escapeAttr = (s) => {
  if (s == null) return "";
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
};

const buildScreenshotUrl = (raw) => {
  if (raw == null) return "";
  const s = typeof raw === "string" ? raw : raw?.url;
  if (s == null || typeof s !== "string") return "";
  const t = s.trim();
  if (!t) return "";
  if (/^data:image\//i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `https:${t}`;
  if (typeof window !== "undefined" && t.startsWith("/")) {
    return `${window.location.origin}${t}`;
  }
  return t;
};

/** Normalize store shape: object keyed by artboard id, or API-style array of { url }. */
const normalizeScreenshotsMap = (shots) => {
  if (shots == null) return {};
  if (Array.isArray(shots)) {
    const out = {};
    shots.forEach((item, i) => {
      const url = typeof item === "string" ? item : item?.url;
      if (url != null && String(url).trim() !== "") out[String(i + 1)] = String(url).trim();
    });
    return out;
  }
  if (typeof shots === "object") return shots;
  return {};
};

const screenshotCellForHtml = (src) => {
  if (!src) {
    return `<span style="color:#888;font-size:12px;">No screenshot</span>`;
  }
  if (/^data:image\//i.test(src)) {
    return `<span style="color:#555;font-size:12px;line-height:1.35;">Screenshot is a local editor capture. Google Docs/Sheets usually cannot embed it—use the <b>Preview</b> link in the row below, or paste into an app that keeps HTML from this page.</span>`;
  }
  return `<img src="${escapeAttr(src)}" alt="Screenshot" width="144" height="100" style="width:144px;height:100px;object-fit:cover;display:block;margin:0 auto;" />`;
};

const sortedArtboardIds = (artboards) => {
  if (artboards == null || typeof artboards !== "object") return [];
  return Object.keys(artboards).sort((a, b) => Number(a) - Number(b));
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const copyDynamicTableToClipboard = (projectKey, screenshots, artboards, storyboardFields) => {
  if (!projectKey) {
    alert("Project is not loaded yet; try again in a moment.");
    return;
  }
  if (artboards == null || typeof artboards !== "object") {
    alert("No storyboard data to copy.");
    return;
  }

  const ids = sortedArtboardIds(artboards);
  if (ids.length === 0) {
    alert("No scenes to copy.");
    return;
  }

  const shots = normalizeScreenshotsMap(screenshots);
  const fields = storyboardFields != null && typeof storyboardFields === "object" ? storyboardFields : {};

  const shareBase = typeof window !== "undefined" ? window.location.origin : "https://mbxr.io";

  const tableFragments = chunkArray(ids, 4).map(
    (rowIds) => `
    <table border="1" style="border-collapse: collapse; table-layout: fixed; width: auto; margin-bottom: 16px; text-align: center; display: inline-table;">
  <tbody>
    <tr>
      ${rowIds
        .map((id) => {
          const key = String(id);
          const src = buildScreenshotUrl(shots[key] ?? shots[id]);
          const inner = screenshotCellForHtml(src);
          return `<td style="width: 150px; padding: 4px;">${inner}</td>`;
        })
        .join("")}
    </tr>
    <tr>
      ${rowIds
        .map((id) => {
          const name = fields[id]?.name ?? "";
          return `<td style="width: 150px; padding: 4px;"><b>${escapeHtml(id)}: ${escapeHtml(name)}</b></td>`;
        })
        .join("")}
    </tr>
    <tr>
      ${rowIds
        .map((id) => {
          const description = fields[id]?.description ?? "";
          return `<td style="width: 150px; padding: 4px;">${escapeHtml(description)}</td>`;
        })
        .join("")}
    </tr>
    <tr>
      ${rowIds
        .map((id) => {
          const href = `${shareBase}/share/${encodeURIComponent(projectKey)}?artboard=${encodeURIComponent(id)}`;
          return `<td style="width: 150px; padding: 4px;"><a target="_blank" href="${escapeAttr(href)}">View preview</a></td>`;
        })
        .join("")}
    </tr>
  </tbody>
</table>`
  );

  const tableHTML = tableFragments.join("");
  const fullHtml = `<!DOCTYPE html><html xmlns="http://www.w3.org/1999/xhtml"><head><meta charset="utf-8" /></head><body>${tableHTML}</body></html>`;

  const plainLines = [];
  plainLines.push(["Scene", "Name", "Description", "Screenshot", "Preview URL"].join("\t"));
  ids.forEach((id) => {
    const key = String(id);
    const name = fields[id]?.name ?? "";
    const description = (fields[id]?.description ?? "").replace(/\r?\n/g, " ");
    const shotRaw = buildScreenshotUrl(shots[key] ?? shots[id]);
    let shotCol = "";
    if (!shotRaw) shotCol = "No screenshot";
    else if (/^data:image\//i.test(shotRaw)) shotCol = "(editor capture — use Preview URL)";
    else shotCol = shotRaw;
    const href = `${shareBase}/share/${projectKey}?artboard=${id}`;
    plainLines.push([id, name, description, shotCol, href].join("\t"));
  });
  const plainText = plainLines.join("\n");

  const writePlain = () =>
    navigator.clipboard.writeText(plainText).catch(() => {
      alert("Failed to copy the storyboard table to the clipboard.");
    });

  if (typeof ClipboardItem === "undefined") {
    writePlain();
    return;
  }

  const htmlBlob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
  const plainBlob = new Blob([plainText], { type: "text/plain;charset=utf-8" });

  const tryWrite = (items) => navigator.clipboard.write(items);

  tryWrite([new ClipboardItem({ "text/html": htmlBlob })])
    .catch(() => tryWrite([new ClipboardItem({ "text/html": htmlBlob, "text/plain": plainBlob })]))
    .catch(() => writePlain());
};

export const CopyStoryboardTable = ({ projectKey: projectKeyProp, storyboardToolbar = false }) => {
  const { screenshots, artboards, storyboardFields, projectKey: projectKeyFromStore } = useObjectStore();
  const projectKey = projectKeyProp ?? projectKeyFromStore;

  const handleCopy = () => {
    copyDynamicTableToClipboard(projectKey, screenshots, artboards, storyboardFields);
  };

  const [hover, setHover] = useState(false);

  if (storyboardToolbar) {
    return (
      <StoryboardToolbarIcon label="Copy as table" onClick={() => handleCopy()}>
        <IconTable size={20} />
      </StoryboardToolbarIcon>
    );
  }

  return (
    <Tooltip label="Copy as a Table">
      <ActionIcon
        style={{ marginLeft: 10, height: 25 }}
        size={"sm"}
        variant="transparent"
        color={!hover ? "#424242" : "#868e96"}
        onPointerOver={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onClick={() => handleCopy()}
      >
        <IconTable size={22} />
      </ActionIcon>
    </Tooltip>
  );
};

export const CopyPreviewEmbed = ({ projectKey, storyboardToolbar = false }) => {
  const copyIframeCode = (page) => {
    const code = `<iframe src="https://mbxr.io/embed-${page}/${projectKey}" width="900" height="400" style="border: none;"></iframe>`;
    navigator.clipboard.writeText(code);
  };

  const [hover, setHover] = useState(false);

  if (storyboardToolbar) {
    return (
      <StoryboardToolbarIcon label="Copy preview iframe" onClick={() => copyIframeCode("share")}>
        <IconPresentation size={20} />
      </StoryboardToolbarIcon>
    );
  }

  return (
    <Tooltip label="Copy Preview iframe">
      <ActionIcon
        style={{ marginLeft: 10, height: 25 }}
        size={"sm"}
        variant="transparent"
        color={!hover ? "#424242" : "#868e96"}
        onPointerOver={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onClick={() => copyIframeCode("share")}
      >
        <IconPresentation size={22} />
      </ActionIcon>
    </Tooltip>
  );
};

export const CopyStoryboardEmbed = ({ projectKey, storyboardToolbar = false }) => {
  const copyIframeCode = (page) => {
    const code = `<iframe src="https://mbxr.io/embed-${page}/${projectKey}" width="900" height="400" style="border: none;"></iframe>`;
    navigator.clipboard.writeText(code);
  };

  const [hover, setHover] = useState(false);

  if (storyboardToolbar) {
    return (
      <StoryboardToolbarIcon label="Copy storyboard iframe" onClick={() => copyIframeCode("story")}>
        <IconLayoutGrid size={20} />
      </StoryboardToolbarIcon>
    );
  }

  return (
    <Tooltip label="Copy Storyboard iframe">
      <ActionIcon
        style={{ marginLeft: 10, height: 25 }}
        size={"sm"}
        variant="transparent"
        color={!hover ? "#424242" : "#868e96"}
        onPointerOver={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onClick={() => copyIframeCode("story")}
      >
        <IconLayoutGrid size={22} />
      </ActionIcon>
    </Tooltip>
  );
};

export const CopyPreviewLink = ({ projectKey, storyboardToolbar = false }) => {
  const [hover, setHover] = useState(false);

  if (storyboardToolbar) {
    return (
      <StoryboardToolbarIcon
        label="Copy preview link"
        onClick={() => navigator.clipboard.writeText(`https://mbxr.io/story/${projectKey}`)}
      >
        <IconLink size={20} />
      </StoryboardToolbarIcon>
    );
  }

  return (
    <Tooltip label="Copy Preview Link">
      <ActionIcon
        style={{ marginLeft: 10, height: 25 }}
        size={"sm"}
        variant="transparent"
        color={!hover ? "#424242" : "#868e96"}
        onPointerOver={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        onClick={() => navigator.clipboard.writeText(`https://mbxr.io/story/${projectKey}`)}
      >
        <IconLink size={22} />
      </ActionIcon>
    </Tooltip>
  );
};
